
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import * as Slider from "@radix-ui/react-slider";
import loadLevels from "./levels";
import { renderGlossaryText } from "./components/GlossaryTerm";

const BASE_TEAM_SIZE = 5;
const ICON_VARIANTS = ["fa-person-dress", "fa-person"];
const SKIN_TONES = ["#f6d7b0", "#e1b07e", "#c68642", "#8d5524", "#d9a074", "#a1662f"];
const ICON_SIZE_ESTIMATE = 32;
const ICON_GAP_ESTIMATE = 12;
const SPRITE_COLUMNS = [0, 512, 1024];
const SPRITE_ROWS = [0, 256, 576, 896, 1216, 1536];
const SPRITE_TRIM = { top: 12, right: 12, bottom: 12, left: 12 };
const baseAssetUrl = (import.meta.env?.BASE_URL ?? "/").replace(/\/*$/, "/");
const SPRITE_SRC = `${baseAssetUrl}assets/backgrounds_2x5.png`;
const DEFAULT_VIEWPORT_INSET = 16;
const imageLoadCache = new Map();
const viewportRenderCache = new Map();

const clampValue = (value, min, max) => Math.min(Math.max(value, min), max);

const toInsetNumber = (value, fallback) => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return Math.max(0, value);
  }
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return Math.max(0, parsed);
    }
  }
  return fallback;
};

const ensureInsetSides = (inset) => {
  const fallback = {
    top: DEFAULT_VIEWPORT_INSET,
    right: DEFAULT_VIEWPORT_INSET,
    bottom: DEFAULT_VIEWPORT_INSET,
    left: DEFAULT_VIEWPORT_INSET,
  };
  if (!inset || typeof inset !== "object") {
    return fallback;
  }
  return {
    top: toInsetNumber(inset.top ?? inset.y ?? inset.vertical, DEFAULT_VIEWPORT_INSET),
    right: toInsetNumber(inset.right ?? inset.x ?? inset.horizontal, DEFAULT_VIEWPORT_INSET),
    bottom: toInsetNumber(inset.bottom ?? inset.y ?? inset.vertical, DEFAULT_VIEWPORT_INSET),
    left: toInsetNumber(inset.left ?? inset.x ?? inset.horizontal, DEFAULT_VIEWPORT_INSET),
  };
};

const loadImageAsset = (src) => {
  if (!src || typeof window === "undefined") {
    return Promise.reject(new Error("No image context"));
  }
  if (imageLoadCache.has(src)) {
    return imageLoadCache.get(src);
  }
  const pending = new Promise((resolve, reject) => {
    const image = new Image();
    image.decoding = "async";
    image.onload = () => resolve(image);
    image.onerror = (error) => reject(error ?? new Error(`Failed to load ${src}`));
    image.src = src;
  }).catch((error) => {
    imageLoadCache.delete(src);
    throw error;
  });
  imageLoadCache.set(src, pending);
  return pending;
};

const getViewportCacheKey = (config) =>
  JSON.stringify({
    src: config?.src ?? "",
    layout: config?.layout ?? null,
    inset: config?.inset ?? null,
  });

const computeViewportFromConfig = (image, config) => {
  const layout = config?.layout;
  if (!layout) {
    return null;
  }

  switch (layout.type) {
    case "grid": {
      const columns = Math.max(1, Math.round(layout.columns ?? layout.cols ?? 1));
      const rows = Math.max(1, Math.round(layout.rows ?? 1));
      const column = clampValue(Math.round(layout.column ?? 0), 0, columns - 1);
      const row = clampValue(Math.round(layout.row ?? 0), 0, rows - 1);
      const cellWidth = image.naturalWidth / columns;
      const cellHeight = image.naturalHeight / rows;

      let sourceX = cellWidth * column;
      let sourceY = cellHeight * row;
      let sourceWidth = cellWidth;
      let sourceHeight = cellHeight;

      const inset = ensureInsetSides(config.inset);
      sourceX += inset.left;
      sourceY += inset.top;
      sourceWidth -= inset.left + inset.right;
      sourceHeight -= inset.top + inset.bottom;

      const maxX = Math.max(0, image.naturalWidth - 1);
      const maxY = Math.max(0, image.naturalHeight - 1);
      sourceX = clampValue(sourceX, 0, maxX);
      sourceY = clampValue(sourceY, 0, maxY);
      const remainingWidth = Math.max(1, image.naturalWidth - sourceX);
      const remainingHeight = Math.max(1, image.naturalHeight - sourceY);

      sourceWidth = Math.max(1, Math.min(remainingWidth, sourceWidth));
      sourceHeight = Math.max(1, Math.min(remainingHeight, sourceHeight));

      return {
        x: sourceX,
        y: sourceY,
        width: sourceWidth,
        height: sourceHeight,
      };
    }
    default:
      return null;
  }
};

const renderBackgroundViewport = async (config) => {
  if (!config?.src || typeof window === "undefined" || typeof document === "undefined") {
    return null;
  }

  const cacheKey = getViewportCacheKey(config);
  if (viewportRenderCache.has(cacheKey)) {
    return viewportRenderCache.get(cacheKey);
  }

  const pending = loadImageAsset(config.src)
    .then((image) => {
      const viewport = computeViewportFromConfig(image, config);
      if (!viewport) {
        return null;
      }
      const canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.round(viewport.width));
      canvas.height = Math.max(1, Math.round(viewport.height));
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        return null;
      }
      ctx.drawImage(
        image,
        viewport.x,
        viewport.y,
        viewport.width,
        viewport.height,
        0,
        0,
        canvas.width,
        canvas.height,
      );
      return canvas.toDataURL("image/png");
    })
    .catch(() => {
      viewportRenderCache.delete(cacheKey);
      return null;
    });

  viewportRenderCache.set(cacheKey, pending);
  return pending;
};

const coerceNumber = (value, fallback) => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === "string") {
    const numericMatch = value.match(/[-+]?\d*\.?\d+/);
    if (numericMatch) {
      const parsed = Number.parseFloat(numericMatch[0]);
      if (Number.isFinite(parsed)) {
        return parsed;
      }
    }
  }

  return fallback;
};

function DeveloperPictograph({ effective, baseCount = BASE_TEAM_SIZE }) {
  const baseTeamSize = Math.max(1, Math.round(coerceNumber(baseCount, BASE_TEAM_SIZE)));
  const effectiveNumber = Math.max(
    coerceNumber(effective, baseTeamSize),
    baseTeamSize
  );
  const extraCapacity = Math.max(effectiveNumber - baseTeamSize, 0);
  const extrasPerDeveloper = baseTeamSize
    ? Math.floor(extraCapacity / baseTeamSize)
    : 0;
  const remainderExtrasRaw = extraCapacity - extrasPerDeveloper * baseTeamSize;
  const remainderInteger = Math.max(0, Math.floor(remainderExtrasRaw + 1e-6));
  const extraGroupsWithFullBonus = Math.min(baseTeamSize, remainderInteger);
  const fractionalExtra = Math.max(
    0,
    Math.min(1, remainderExtrasRaw - remainderInteger)
  );
  const partialIndex = Math.min(baseTeamSize - 1, remainderInteger);

  const density = baseTeamSize ? effectiveNumber / baseTeamSize : 1;
  const iconScale = (() => {
    if (!Number.isFinite(density) || density <= 1.05) {
      return 1;
    }
    const scale = 1.15 / Math.log2(density + 1.2);
    return Math.max(0.3, Math.min(1, scale));
  })();
  const iconSizeEstimate = ICON_SIZE_ESTIMATE * iconScale;
  const iconGapEstimate = ICON_GAP_ESTIMATE * iconScale * 0.85;
  const rowGapEstimate = ICON_GAP_ESTIMATE * iconScale * 0.9;
  const barStyle = {
    "--developer-icon-size": `${Math.max(12, Math.round(iconSizeEstimate))}px`,
    "--developer-icon-gap": `${Math.max(4, Math.round(iconGapEstimate))}px`,
    "--developer-group-gap": `${Math.max(4, Math.round(iconGapEstimate * 0.7))}px`,
    "--developer-row-gap": `${Math.max(6, Math.round(rowGapEstimate))}px`,
  };

  let extraIconCursor = 0;
  const nextExtraIconClass = () => {
    const icon = ICON_VARIANTS[extraIconCursor % ICON_VARIANTS.length];
    extraIconCursor += 1;
    return icon;
  };

  return (
    <div className="developer-bar" aria-hidden="true" style={barStyle}>
      {Array.from({ length: baseTeamSize }).map((_, index) => {
        const baseIconClass = ICON_VARIANTS[index % ICON_VARIANTS.length];
        const extrasForGroup =
          extrasPerDeveloper + (index < extraGroupsWithFullBonus ? 1 : 0);
        const includePartial =
          fractionalExtra > 0.001 && index === partialIndex;
        const iconsInGroup = 1 + extrasForGroup + (includePartial ? 1 : 0);
        const estimatedWidth = Math.min(
          iconsInGroup * iconSizeEstimate +
            Math.max(0, iconsInGroup - 1) * iconGapEstimate +
            16,
          360
        );

        return (
          <div
            className="developer-group"
            key={`developer-${index}`}
            style={{ flexBasis: `${estimatedWidth}px` }}
          >
            <i
              className={`fa-solid ${baseIconClass} developer-icon developer-icon--base`}
              style={{ color: SKIN_TONES[index % SKIN_TONES.length] }}
            />
            {Array.from({ length: extrasForGroup }).map((__, extraIndex) => {
              const extraIconClass = nextExtraIconClass();
              return (
                <i
                  key={`extra-${index}-${extraIndex}`}
                  className={`fa-solid ${extraIconClass} developer-icon developer-icon--extra`}
                />
              );
            })}
            {includePartial && (
              <i
                className={`fa-solid ${nextExtraIconClass()} developer-icon developer-icon--extra developer-icon--partial`}
                style={{ "--fill": fractionalExtra.toString() }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function App() {
  const [levels, setLevels] = useState([]);
  const [meta, setMeta] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [level, setLevel] = useState(0);
  const [spriteBackgrounds, setSpriteBackgrounds] = useState([]);
  const [customBackgrounds, setCustomBackgrounds] = useState({});
  const [activeDetailId, setActiveDetailId] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isMobileLandscape, setIsMobileLandscape] = useState(false);

  const glossaryMap = useMemo(() => {
    if (!meta?.glossary || typeof meta.glossary !== 'object') {
      return {};
    }
    // Build map that supports both primary terms and aliases
    const map = {};
    Object.entries(meta.glossary).forEach(([term, definition]) => {
      if (typeof definition === 'string') {
        // Simple string definition - add primary term and any aliases
        map[term] = { definition, canonical: term };
      } else if (typeof definition === 'object' && definition !== null) {
        // Object format with definition and aliases
        const def = definition.definition || '';
        map[term] = { definition: def, canonical: term };

        // Add aliases pointing back to the canonical term
        if (Array.isArray(definition.aliases)) {
          definition.aliases.forEach((alias) => {
            map[alias] = { definition: def, canonical: term };
          });
        }
      }
    });
    return map;
  }, [meta]);

  useEffect(() => {
    let canceled = false;

    loadLevels()
      .then((data) => {
        if (!canceled) {
          if (Array.isArray(data)) {
            setLevels(data);
            setMeta(null);
          } else {
            setLevels(Array.isArray(data?.levels) ? data.levels : []);
            setMeta(data?.meta ?? null);
          }
          setIsLoading(false);
        }
      })
      .catch((error) => {
        console.error("Failed to load levels", error);
        if (!canceled) {
          setLoadError(error);
          setIsLoading(false);
        }
      });

    return () => {
      canceled = true;
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    let canceled = false;
    const image = new Image();
    image.onload = () => {
      if (canceled) return;
      const tiles = [];
      for (let row = 0; row < SPRITE_ROWS.length - 1; row += 1) {
        const rowStart = SPRITE_ROWS[row];
        const rowEnd = SPRITE_ROWS[row + 1];
        const rowHeight = rowEnd - rowStart;
        const rowTopTrim = row === 0 ? 6 : SPRITE_TRIM.top;
        const rowBottomTrim = row === 0 ? 10 : SPRITE_TRIM.bottom;
        const sourceY = rowStart + rowTopTrim;
        const sourceHeight = rowHeight - rowTopTrim - rowBottomTrim;

        for (let col = 0; col < SPRITE_COLUMNS.length - 1; col += 1) {
          const colStart = SPRITE_COLUMNS[col];
          const colEnd = SPRITE_COLUMNS[col + 1];
          const colWidth = colEnd - colStart;
          const sourceX = colStart + SPRITE_TRIM.left;
          const sourceWidth = colWidth - SPRITE_TRIM.left - SPRITE_TRIM.right;

          if (sourceWidth <= 0 || sourceHeight <= 0) {
            tiles.push(undefined);
            continue;
          }

          const canvas = document.createElement("canvas");
          canvas.width = sourceWidth;
          canvas.height = sourceHeight;
          const ctx = canvas.getContext("2d");
          if (!ctx) {
            tiles.push(undefined);
            continue;
          }

          ctx.drawImage(
            image,
            sourceX,
            sourceY,
            sourceWidth,
            sourceHeight,
            0,
            0,
            sourceWidth,
            sourceHeight
          );

          tiles.push(canvas.toDataURL("image/png"));
        }
      }

      setSpriteBackgrounds(tiles);
    };

    image.onerror = () => {
      if (!canceled) {
        setSpriteBackgrounds([]);
      }
    };

    image.src = SPRITE_SRC;

    return () => {
      canceled = true;
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    if (!levels.length) {
      setCustomBackgrounds({});
      return undefined;
    }

    let canceled = false;

    const loadCustomTiles = async () => {
      const pairs = await Promise.all(
        levels.map(async (entry) => {
          if (!entry?.background_image) {
            return null;
          }
          const dataUrl = await renderBackgroundViewport(entry.background_image).catch(() => null);
          if (!dataUrl) {
            return null;
          }
          return { id: entry.id, dataUrl };
        }),
      );

      if (canceled) {
        return;
      }

      const mapped = pairs.reduce((acc, item) => {
        if (item) {
          acc[item.id] = item.dataUrl;
        }
        return acc;
      }, {});
      setCustomBackgrounds(mapped);
    };

    loadCustomTiles();

    return () => {
      canceled = true;
    };
  }, [levels]);

  const slides = useMemo(() => {
    if (meta) {
      const introTitle = meta.title || "Team Agentification Journey";
      const introBody = meta.rhetoric || "";
      return [
        {
          type: "meta",
          id: "journey-intro",
          title: introTitle,
          body: introBody,
        },
        ...levels,
      ];
    }
    return levels;
  }, [levels, meta]);

  const sliderMax = Math.max(slides.length - 1, 0);

  const clampLevel = (value) => {
    if (!Number.isFinite(value)) return 0;
    return Math.min(Math.max(value, 0), sliderMax);
  };

  const initialLevelAppliedRef = useRef(false);

  useEffect(() => {
    if (!slides.length || initialLevelAppliedRef.current || typeof window === "undefined") {
      return;
    }
    const params = new URLSearchParams(window.location.search);
    const paramValue = params.get("level");
    const numeric = paramValue !== null ? Number(paramValue) : NaN;
    const initial = Number.isFinite(numeric) ? clampLevel(numeric) : 0;
    setLevel(initial);
    initialLevelAppliedRef.current = true;
  }, [slides.length, sliderMax]);

  useEffect(() => {
    if (!slides.length) {
      return;
    }
    setLevel((prev) => clampLevel(prev));
  }, [sliderMax, slides.length]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const mobileQuery = window.matchMedia(
      "(max-height: 600px) and (orientation: landscape)"
    );

    const handleMobileChange = (e) => {
      setIsMobileLandscape(e.matches);
    };

    setIsMobileLandscape(mobileQuery.matches);
    mobileQuery.addEventListener("change", handleMobileChange);

    return () => {
      mobileQuery.removeEventListener("change", handleMobileChange);
    };
  }, []);

  const clampedLevel = Math.min(Math.max(level, 0), sliderMax);
  const activeIndex = Math.round(clampedLevel);
  const sliderValue = sliderMax - clampedLevel;
  const current = slides[activeIndex];
  const isMetaSlide = current?.type === "meta";
  const activeLevel = !isMetaSlide && current ? current : null;
  const originalDevelopers = activeLevel?.original_developers ?? BASE_TEAM_SIZE;
  const tools = Array.isArray(activeLevel?.tools) ? activeLevel.tools : [];
  const milestones = Array.isArray(activeLevel?.milestones) ? activeLevel.milestones : [];
  const risks = Array.isArray(activeLevel?.risks) ? activeLevel.risks : [];
  const challenges = Array.isArray(activeLevel?.challenges) ? activeLevel.challenges : [];
  const bestPractices = Array.isArray(activeLevel?.best_practices)
    ? activeLevel.best_practices
    : [];
  const metrics = Array.isArray(activeLevel?.metrics) ? activeLevel.metrics : [];
  const impact = activeLevel?.impact ?? null;
  const [isContentHidden, setIsContentHidden] = useState(false);

  const effectiveDisplay = (() => {
    if (!activeLevel) return "";
    const value = activeLevel.effective_developers;
    if (typeof value === "number") {
      return Number.isInteger(value) ? value : value.toFixed(1);
    }
    if (typeof value === "string") {
      return value;
    }
    return String(value ?? "");
  })();

  const detailSections = useMemo(() => {
    if (!activeLevel) {
      return [];
    }
    const sections = [];

    // Add effective developers as first section on mobile only
    if (isMobileLandscape) {
      sections.push({
        id: "effective-developers",
        title: "Team Size",
        type: "developer-metric",
        items: [{
          effective: activeLevel.effective_developers,
          baseCount: originalDevelopers,
          display: effectiveDisplay
        }],
      });
    }

    const teamCompetencies = Object.entries(activeLevel?.team_competencies ?? {});

    if (teamCompetencies.length > 0) {
      sections.push({
        id: "team-competencies",
        title: "Team Competencies",
        type: "competencies",
        items: teamCompetencies,
      });
    }

    if (tools.length > 0) {
      sections.push({
        id: "tools",
        title: "Tool Stack",
        type: "tools",
        items: tools,
      });
    }

    if (impact) {
      sections.push({
        id: "impact",
        title: "Impact",
        type: "text",
        items: [impact],
      });
    }

    if (milestones.length > 0) {
      sections.push({
        id: "milestones",
        title: "Milestones",
        type: "list",
        items: milestones,
      });
    }

    if (risks.length > 0) {
      sections.push({
        id: "risks",
        title: "Risks",
        type: "list",
        items: risks,
      });
    }

    if (challenges.length > 0) {
      sections.push({
        id: "challenges",
        title: "Challenges",
        type: "challenges",
        items: challenges,
      });
    }

    if (bestPractices.length > 0) {
      sections.push({
        id: "best-practices",
        title: "Best Practices",
        type: "list",
        items: bestPractices,
      });
    }

    if (metrics.length > 0) {
      sections.push({
        id: "metrics",
        title: "Key Metrics",
        type: "list",
        items: metrics,
      });
    }

    return sections;
  }, [activeLevel, tools, impact, milestones, risks, challenges, bestPractices, metrics, isMobileLandscape, originalDevelopers, effectiveDisplay]);

  const hasDetailContent = detailSections.length > 0;

  useEffect(() => {
    if (!hasDetailContent) {
      setActiveDetailId(null);
      return;
    }
    setActiveDetailId((prev) => {
      if (prev && detailSections.some((section) => section.id === prev)) {
        return prev;
      }
      return detailSections[0]?.id ?? null;
    });
  }, [hasDetailContent, detailSections]);

  const activeDetail = hasDetailContent
    ? detailSections.find((section) => section.id === activeDetailId) ?? detailSections[0]
    : null;

  const renderDetailCards = (section) => {
    if (!section) {
      return null;
    }

    switch (section.type) {
      case "developer-metric":
        return section.items.map((item, index) => (
          <div className="detail-card detail-card--developer-metric" key={`dev-metric-${index}`}>
            <div className="developer-metric__header">
              <strong>Effective Developers:</strong>
              <span className="developer-metric__value">{item.display}</span>
            </div>
            <DeveloperPictograph
              effective={item.effective}
              baseCount={item.baseCount}
            />
          </div>
        ));

      case "competencies":
        return (
          <div className="detail-card detail-card--competencies" key="competencies">
            <dl className="competency-grid">
              {section.items.map(([key, value]) => {
                const label = key.replace(/_/g, " ");
                const valueText =
                  typeof value === "number"
                    ? Number.isInteger(value)
                      ? value
                      : value.toFixed(1)
                    : String(value ?? "");
                return (
                  <div className="competency-grid__item" key={`competency-${key}`}>
                    <dt>{label}</dt>
                    <dd>{valueText}</dd>
                  </div>
                );
              })}
            </dl>
          </div>
        );

      case "tools":
        return section.items.map((tool, index) => {
          const badgeText = typeof tool.type === "string" ? tool.type : "";
          const badgeVariant = badgeText.toLowerCase().replace(/[^a-z0-9]+/g, "-");
          return (
            <div className="detail-card detail-card--tool" key={`tool-${index}`}>
              <div className="detail-card__tool-header">
                <span className="detail-card__tool-name">{tool.name}</span>
                {badgeText && (
                  <span className={`detail-card__tool-badge detail-card__tool-badge--${badgeVariant}`}>
                    {badgeText}
                  </span>
                )}
              </div>
              {tool.note && <p className="detail-card__body">{renderGlossaryText(tool.note, glossaryMap)}</p>}
            </div>
          );
        });

      case "text":
        return section.items.map((item, index) => (
          <div className="detail-card detail-card--text" key={`text-${index}`}>
            <p className="detail-card__body">{renderGlossaryText(item, glossaryMap)}</p>
          </div>
        ));

      case "list":
        return section.items.map((item, index) => (
          <div className="detail-card detail-card--list" key={`${section.id}-${index}`}>
            <p className="detail-card__body">{renderGlossaryText(item, glossaryMap)}</p>
          </div>
        ));

      case "challenges":
        return section.items.map((challenge, index) => (
          <div className="detail-card detail-card--challenge" key={`challenge-${index}`}>
            {challenge.problem && (
              <p className="detail-challenge__problem">
                <strong>Problem:</strong> {renderGlossaryText(challenge.problem, glossaryMap)}
              </p>
            )}
            {challenge.solution && (
              <p className="detail-challenge__solution">
                <strong>Solution:</strong> {renderGlossaryText(challenge.solution, glossaryMap)}
              </p>
            )}
          </div>
        ));

      default:
        return null;
    }
  };

  const positionForIndex = (index) => {
    if (sliderMax === 0) return 50;
    return (index / sliderMax) * 100;
  };

  const gradientLayer = "linear-gradient(180deg, rgba(64, 68, 78, 0.06), rgba(24, 26, 34, 0.15))";
  const backgroundIndex = activeLevel
    ? meta
      ? activeIndex - 1
      : activeIndex
    : -1;
  const fallbackBackground = backgroundIndex >= 0 ? spriteBackgrounds[backgroundIndex] : undefined;
  const customBackground = activeLevel ? customBackgrounds[activeLevel.id] : undefined;
  const activeBackground = customBackground ?? fallbackBackground;
  const contentStyle = isMetaSlide
    ? {
        backgroundColor: "#05060c",
        backgroundImage: "none",
      }
    : activeBackground
      ? {
          backgroundImage: `${gradientLayer}, url('${activeBackground}')`,
          backgroundRepeat: "no-repeat, no-repeat",
          backgroundSize: "100% 100%, cover",
          backgroundPosition: "center, center",
          backgroundColor: "#05060c",
        }
      : {
          backgroundImage: gradientLayer,
          backgroundColor: "#05060c",
        };

  useEffect(() => {
    if (!slides.length || typeof window === "undefined") {
      return;
    }
    const params = new URLSearchParams(window.location.search);
    const target = String(activeIndex);
    if (params.get("level") === target) {
      return;
    }
    params.set("level", target);
    const newQuery = params.toString();
    const newUrl = `${window.location.pathname}?${newQuery}${window.location.hash}`;
    window.history.replaceState(null, "", newUrl);
  }, [activeIndex, slides.length]);
  const toggleContentHidden = useCallback(() => {
    setIsContentHidden((prev) => !prev);
  }, []);

  const toggleSidebar = useCallback(() => {
    setIsSidebarOpen((prev) => !prev);
  }, []);

  const touchStartRef = useRef(null);
  const touchEndRef = useRef(null);

  const handleTouchStart = useCallback((e) => {
    touchStartRef.current = e.touches[0].clientY;
  }, []);

  const handleTouchMove = useCallback((e) => {
    touchEndRef.current = e.touches[0].clientY;
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (!touchStartRef.current || !touchEndRef.current) return;

    const swipeDistance = touchStartRef.current - touchEndRef.current;
    const minSwipeDistance = 50; // Minimum pixels to trigger swipe

    if (Math.abs(swipeDistance) > minSwipeDistance) {
      if (swipeDistance > 0) {
        // Swiped up - go to next level (down the timeline)
        setLevel((prev) => Math.min(prev + 1, sliderMax));
      } else {
        // Swiped down - go to previous level (up the timeline)
        setLevel((prev) => Math.max(prev - 1, 0));
      }
    }

    touchStartRef.current = null;
    touchEndRef.current = null;
  }, [sliderMax]);

  return (
    <div className={`app-shell${isSidebarOpen ? " sidebar-open" : ""}`}>
      <div className="rotate-prompt">
        <div className="rotate-prompt__content">
          <i className="fa-solid fa-rotate rotate-prompt__icon"></i>
          <p className="rotate-prompt__text">Please rotate your device to landscape</p>
        </div>
      </div>
      <button
        type="button"
        className="sidebar-toggle"
        onClick={toggleSidebar}
        aria-label={isSidebarOpen ? "Close sidebar" : "Open sidebar"}
        aria-expanded={isSidebarOpen}
      >
        <i className={`fa-solid ${isSidebarOpen ? "fa-chevron-left" : "fa-chevron-right"}`} />
      </button>
      <aside className={`sidebar${isSidebarOpen ? " is-open" : ""}`}>
        <div className="slider-stack">
          <Slider.Root
            className="timeline-slider"
            orientation="vertical"
            min={0}
            max={sliderMax || 0.0001}
            step={0.01}
            value={[sliderValue]}
            onValueChange={(value) =>
              setLevel(sliderMax - (value[0] ?? 0))
            }
            aria-label="Team agentification stage"
          >
            <Slider.Track className="timeline-track">
              <Slider.Range className="timeline-range" />
            </Slider.Track>
            <Slider.Thumb className="timeline-thumb" />
          </Slider.Root>
          <div className="slider-stops">
            {slides.map((item, index) => {
              const isMetaStop = item?.type === "meta";
              const key = item?.id || item?.slug || `slide-${index}`;
              const levelLabel = !isMetaStop && typeof item?.level !== "undefined"
                ? `Level ${item.level}`
                : item?.title || `Slide ${index + 1}`;
              return (
                <div
                  key={key}
                  className={`slider-stop ${index === activeIndex ? "is-active" : ""}`}
                  style={{ top: `${positionForIndex(index)}%` }}
                >
                  {isMetaStop ? (
                    <button
                      type="button"
                      className="slider-meta-button"
                      onClick={() => setLevel(index)}
                      aria-label={item.title || "Journey introduction"}
                    >
                      Intro
                    </button>
                  ) : (
                    item.thumbnail ? (
                      <button
                        type="button"
                        className="slider-thumbnail-button"
                        onClick={() => setLevel(index)}
                        aria-label={`Select ${item.title}`}
                      >
                        <img
                          className="slider-thumbnail"
                          src={item.thumbnail}
                          alt={`${item.title} thumbnail`}
                        />
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="slider-level-button"
                        onClick={() => setLevel(index)}
                        aria-label={levelLabel}
                      >
                        {levelLabel}
                      </button>
                    )
                  )}
                </div>
              );
            })}
          </div>
        </div>
        <button
          type="button"
          className="rhetoric-toggle"
          onClick={toggleContentHidden}
          aria-pressed={isContentHidden}
        >
          {isContentHidden ? "Show Rhetoric" : "Hide Rhetoric"}
        </button>
      </aside>
      {isSidebarOpen && (
        <div
          className="sidebar-overlay"
          onClick={toggleSidebar}
          aria-hidden="true"
        />
      )}
      <main
        className={`content${isContentHidden ? " content--hidden" : ""}`}
        style={contentStyle}
        data-active-index={activeIndex}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div className="content-view">
          {isLoading && (
            <div className="content-status">Loading levels…</div>
          )}
          {loadError && !isLoading && (
            <div className="content-status content-status--error">
              Failed to load levels. {loadError.message}
            </div>
          )}
          {current && !isLoading && !loadError && (
            <AnimatePresence mode="wait">
              <motion.div
                key={current.id || (isMetaSlide ? "intro" : `level-${activeIndex}`)}
                className={`content-pane${isMetaSlide ? " content-pane--intro" : ""}`}
                aria-hidden={isContentHidden}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5 }}
              >
                {isMetaSlide ? (
                  <div className="intro-frame">
                    <div className="intro-card">
                      {current.title && <h1 className="intro-card__title">{current.title}</h1>}
                      {(() => {
                        const bodySegments = current.body ? current.body.split(/\n\s*\n/) : [];
                        if (!bodySegments.length) {
                          return current.body ? (
                            <p className="intro-card__body">{renderGlossaryText(current.body, glossaryMap)}</p>
                          ) : null;
                        }
                        return bodySegments
                          .map((paragraph) => paragraph.trim())
                          .filter(Boolean)
                          .map((paragraph, idx) => (
                            <p className="intro-card__body" key={`intro-body-${idx}`}>
                              {renderGlossaryText(paragraph, glossaryMap)}
                            </p>
                          ));
                      })()}
                    </div>
                  </div>
                ) : (
                  <div className="level-layout">
                    <div className="level-header">
                      <div className="level-header__copy">
                        <h1>{activeLevel.title}</h1>
                        <h2>{renderGlossaryText(activeLevel.subtitle, glossaryMap)}</h2>
                        <p>{renderGlossaryText(activeLevel.description, glossaryMap)}</p>
                        <div className="level-meta">
                          <p className="level-meta__item">
                            <strong>Agent Role:</strong> {renderGlossaryText(activeLevel.agent_role, glossaryMap)}
                          </p>
                          <p className="level-meta__item">
                            <strong>Traits:</strong> {renderGlossaryText(activeLevel.traits.join(", "), glossaryMap)}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="developer-metric">
                      <div className="developer-metric__header">
                        <strong>Effective Developers:</strong>
                        <span className="developer-metric__value">{effectiveDisplay}</span>
                      </div>
                      <DeveloperPictograph
                        effective={activeLevel.effective_developers}
                        baseCount={originalDevelopers}
                      />
                    </div>
                    {hasDetailContent && activeDetail && (
                      <div className="detail-section">
                      <div className={`detail-cards detail-cards--${activeDetail.type}`}>
                        {renderDetailCards(activeDetail)}
                      </div>
                      <div className="detail-tabs" role="tablist" aria-label="Level details">
                        {detailSections.map((section) => (
                          <button
                            key={section.id}
                            type="button"
                            role="tab"
                            aria-selected={section.id === activeDetail.id}
                            className={`detail-tab ${section.id === activeDetail.id ? "is-active" : ""}`}
                            onClick={() => setActiveDetailId(section.id)}
                          >
                            {section.title}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                )}
              </motion.div>
            </AnimatePresence>
          )}
        </div>
      </main>
    </div>
  );
}
